import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type TriggerReason = "limit_hit" | "bulk_action" | "after_conversions" | "manual";

interface FunnelState {
  isUpsellOpen: boolean;
  isDownsellOpen: boolean;
  triggerReason: TriggerReason | null;
  conversionCount: number;
}

interface FunnelContextType extends FunnelState {
  showUpsell: (reason: TriggerReason) => void;
  hideUpsell: () => void;
  showDownsell: () => void;
  hideDownsell: () => void;
  declineUpsell: () => void;
  dismissAll: () => void;
  trackConversion: () => void;
}

const FunnelContext = createContext<FunnelContextType | undefined>(undefined);

const DISMISSED_KEY = "funnel_dismissed_until";

function isDismissed(): boolean {
  const dismissed = localStorage.getItem(DISMISSED_KEY);
  if (!dismissed) return false;
  return new Date(dismissed) > new Date();
}

function setDismissedFor24Hours(): void {
  const tomorrow = new Date();
  tomorrow.setHours(tomorrow.getHours() + 24);
  localStorage.setItem(DISMISSED_KEY, tomorrow.toISOString());
}

interface FunnelProviderProps {
  children: ReactNode;
}

/**
 * FunnelProvider - Context provider for managing sales funnel state
 * 
 * @example
 * // Wrap your app with FunnelProvider
 * <FunnelProvider>
 *   <App />
 * </FunnelProvider>
 */
export function FunnelProvider({ children }: FunnelProviderProps) {
  const [state, setState] = useState<FunnelState>({
    isUpsellOpen: false,
    isDownsellOpen: false,
    triggerReason: null,
    conversionCount: 0,
  });

  const showUpsell = useCallback((reason: TriggerReason) => {
    if (isDismissed()) return;
    setState((prev) => ({
      ...prev,
      isUpsellOpen: true,
      isDownsellOpen: false,
      triggerReason: reason,
    }));
  }, []);

  const hideUpsell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isUpsellOpen: false,
    }));
  }, []);

  const showDownsell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isUpsellOpen: false,
      isDownsellOpen: true,
    }));
  }, []);

  const hideDownsell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isDownsellOpen: false,
      triggerReason: null,
    }));
  }, []);

  const declineUpsell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isUpsellOpen: false,
      isDownsellOpen: true,
    }));
  }, []);

  const dismissAll = useCallback(() => {
    setDismissedFor24Hours();
    setState((prev) => ({
      ...prev,
      isUpsellOpen: false,
      isDownsellOpen: false,
      triggerReason: null,
    }));
  }, []);

  const trackConversion = useCallback(() => {
    setState((prev) => ({
      ...prev,
      conversionCount: prev.conversionCount + 1,
    }));
  }, []);

  const value: FunnelContextType = {
    ...state,
    showUpsell,
    hideUpsell,
    showDownsell,
    hideDownsell,
    declineUpsell,
    dismissAll,
    trackConversion,
  };

  return (
    <FunnelContext.Provider value={value}>
      {children}
    </FunnelContext.Provider>
  );
}

/**
 * useSalesFunnel - Hook to access and control the sales funnel
 * 
 * @example
 * const { showUpsell, isUpsellOpen } = useSalesFunnel();
 * 
 * // Trigger upsell when limit hit
 * if (!canConvert) {
 *   showUpsell("limit_hit");
 * }
 */
export function useSalesFunnel(): FunnelContextType {
  const context = useContext(FunnelContext);
  if (context === undefined) {
    throw new Error("useSalesFunnel must be used within a FunnelProvider");
  }
  return context;
}
